// Pure builder + serializer for log entries. Stays separate from React
// state so the export schema (SPEC §"Logs") is testable and stable
// regardless of how the UI accumulates the underlying data.

import type {
  AttemptAnomaly,
  DeviceSnapshot,
  LogEntry,
  RunMode,
  ScenarioConfigSnapshot,
  Transition,
  WifiSnapshot,
} from './types.js';
import type { DerivedResult } from './attempt.js';

export interface BuildLogEntryInput {
  readonly id: string;
  readonly startedAtMs: number;
  readonly mode: RunMode;
  readonly scenario: string | null;
  readonly scenarioConfig: ScenarioConfigSnapshot | null;
  readonly device: DeviceSnapshot;
  readonly wifi: WifiSnapshot;
  readonly transitions: readonly Transition[];
  readonly derived: DerivedResult;
  /**
   * Anomalies observed outside the WiFi-status transition stream — e.g. a
   * stored-credential read that failed despite the characteristic claiming
   * read support. Appended after the transition-derived anomalies.
   */
  readonly extraAnomalies?: readonly AttemptAnomaly[];
}

/**
 * Build a finalized LogEntry from an attempt's accumulated state. Caller
 * passes the derived result so the entry reflects the same termination
 * decision the UI showed the user.
 */
export function buildLogEntry(input: BuildLogEntryInput): LogEntry {
  if (input.derived.result === 'pending') {
    throw new Error(
      'Cannot build a log entry from a pending attempt; derive a terminal result first',
    );
  }
  return {
    id: input.id,
    timestamp: new Date(input.startedAtMs).toISOString(),
    mode: input.mode,
    scenario: input.scenario,
    scenarioConfig: input.scenarioConfig,
    device: input.device,
    wifi: input.wifi,
    transitions: [...input.transitions],
    result: input.derived.result,
    failureReason: input.derived.failureReason,
    totalDurationMs: input.derived.totalDurationMs,
    tConnectedMs: input.derived.tConnectedMs,
    tOnInternetMs: input.derived.tOnInternetMs,
    anomalies: [...input.derived.anomalies, ...(input.extraAnomalies ?? [])],
  };
}

/** Pretty-print the session log as JSON for clipboard / download. */
export function serializeLogEntries(entries: readonly LogEntry[]): string {
  return JSON.stringify({ entries }, null, 2);
}

/**
 * Filename used by Download Log. Uses an ISO date with `:` replaced by `-`
 * so it's filesystem-safe on Windows (Rafael's environment).
 */
export function exportFilename(nowMs: number): string {
  const safe = new Date(nowMs).toISOString().replace(/:/g, '-').replace(/\.\d+Z$/, 'Z');
  return `soluu-harness-${safe}.json`;
}

/**
 * SPEC §"Logs": passwordLength, not password, is recorded. Wraps a string
 * count in a tiny helper so the call site can't accidentally log the
 * cleartext.
 */
export function passwordLengthOf(password: string): number {
  return password.length;
}
